import React, { FC, Fragment, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Box } from '@mui/material';
import PageTitle from 'components/shared/page-title/page-title.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import {
    setSubcontractStatusRulesTemplate,
    subcontractRulePartnersTemplate,
    subcontractStatusRulesTemplate,
} from 'apollo/templates/workFlow';
import { Steps, SubcontractStatusRule, SubcontractStatusRuleSet } from 'models/workflow-step';
import Loader from 'components/shared/loader/loader.component';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import PartnerRulesEditor from './partner-rules-editor.component';

type SubcontractorRulesProps = { steps: Steps[] };

type RulePartner = { _id: string; labId: string; lab?: { _id: string; name: string } };

/**
 * DD-3489: per connected partner, which of this lab's own steps an order moves to when the
 * partner's sub-order reaches a standard status. The rules are this lab's alone — the
 * partner never sees them, and they never refer to the partner's workflow.
 */
const SubcontractorRules: FC<SubcontractorRulesProps> = ({ steps }) => {
    const { t: translate } = useTranslation(['workflow', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);

    // External technicians never configure the lab's partner relationships.
    const isExternalUser = permissions.isExternalTechnician(currentUserVar().role);
    const { data: partnerData, loading: isPartnersLoading } = useQuery(subcontractRulePartnersTemplate, {
        skip: isExternalUser,
        onError: showBoundary,
    });
    const { data: ruleData, loading: isRulesLoading } = useQuery(subcontractStatusRulesTemplate, {
        skip: isExternalUser,
        onError: showBoundary,
    });
    const [saveRules, { loading: isSaving }] = useMutation(setSubcontractStatusRulesTemplate, {
        refetchQueries: [{ query: subcontractStatusRulesTemplate }],
        onCompleted: () =>
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('workflow:SUBCONTRACTOR_RULES_SAVED'),
            }),
        onError: showBoundary,
    });

    const partners: RulePartner[] = partnerData?.suppliers ?? [];
    // Memoised so each editor keeps its draft until the saved rules actually change.
    const rulesBySupplier = useMemo(() => {
        const ruleSets: SubcontractStatusRuleSet[] = ruleData?.subcontractStatusRules ?? [];
        return new Map(
            ruleSets.map((ruleSet) => [
                ruleSet.supplierId,
                ruleSet.rules.map(({ status, stepId }) => ({ status, stepId })),
            ]),
        );
    }, [ruleData]);
    const noRules: SubcontractStatusRule[] = useMemo(() => [], []);
    const rulesFor = (partnerId: string) => rulesBySupplier.get(partnerId) ?? noRules;

    const handleSave = (supplierId: string) => (rules: SubcontractStatusRule[]) =>
        saveRules({ variables: { supplierId, rules } });

    if (isExternalUser) {
        return null;
    }
    return (
        <Fragment>
            <Box mt={4}>
                <PageTitle headingStyle="h3" title={translate('workflow:SUBCONTRACTOR_RULES')} />
            </Box>
            <Box mb={2}>{translate('workflow:SUBCONTRACTOR_RULES_DESC')}</Box>
            {isPartnersLoading || isRulesLoading ? <Loader /> : undefined}
            {!isPartnersLoading && !partners.length ? (
                <Alert severity="info">{translate('workflow:NO_SUBCONTRACTORS_CONNECTED')}</Alert>
            ) : undefined}
            {partners.map((partner) => (
                <PartnerRulesEditor
                    key={partner.labId}
                    partnerName={partner.lab?.name ?? ''}
                    steps={steps}
                    rules={rulesFor(partner.labId)}
                    isSaving={isSaving}
                    onSave={handleSave(partner.labId)}
                />
            ))}
        </Fragment>
    );
};

export default SubcontractorRules;
